import crossDomainAuthTemplateUrl from '../../../app/crossdomain/crossDomainAuth.tpl.html';
import { memoize } from 'lodash';
import logger from '../../../common/logger';

const log = logger('CrossDomainAuth');
const error = log.extend('error');

/**
 * Validates and exchanges a cross-domain token for a user token.
 */
function validateAndExchangeToken(token, { security, signalboost, logService }) {
    function respond() {
        if (!token) {
            log('No cross-domain token.');
            return Promise.reject(null);
        }
        logService.logData(
            'INFO',
            'crossDomainAuth -> validateAndExchangeToken: exchanging cross-domain token'
        );
        return signalboost.user.exchangeCrossDomain(token);
    }

    logService.logData(
        'INFO',
        'crossDomainAuth -> validateAndExchangeToken: logging out and clearing the token'
    );

    // remove current auth token, regardless of token validity
    return security.logout().then(respond, respond);
}

/**
 * Exchanges a cross-domain token for a User token (once only).
 * Token exchange is followed by redirecting user to correct location.
 */
const processCrossDomainTokenOnce = memoize(function (
    token,
    { $rootScope, $window, $location, security, signalboost, loginService, ssoRedirect, logService }
) {
    const split = token.split(':::');
    let url = '';
    let actualToken = token;
    if (split !== undefined && split.length > 1) {
        actualToken = split[0];
        url = split[1].replace(/-/g, '+').replace(/_/g, '/').replace(/=/g, '');
    }

    return validateAndExchangeToken(actualToken, { security, signalboost, logService })
        .then(function (result) {
            if (result && result.redirectUrl) {
                logService.logData(
                    'INFO',
                    'crossDomainAuth -> validateAndExchangeToken: received redirect url from api'
                );
                $window.location = result.redirectUrl;
            } else {
                return loginService.initOrgAndUserPostLogin().then(function ({ user }) {
                    $rootScope.$broadcast('current user changed', user);
                    if (!!url) {
                        logService.logData(
                            'INFO',
                            'crossDomainAuth -> initOrgAndUserPostLogin: received url from token',
                            url
                        );
                        $window.location = atob(url);
                    } else {
                        url = ssoRedirect.getAndClear();
                        logService.logData(
                            'INFO',
                            'crossDomainAuth -> initOrgAndUserPostLogin: received redirect url from ssoRedirect',
                            url
                        );
                        if (!!url) {
                            $location.url(url);
                        } else {
                            $location.path('/home');
                        }
                    }
                });
            }
        })
        .catch(function (e) {
            logService.logData(
                'ERROR',
                'crossDomainAuth -> validateAndExchangeToken: error while processing token',
                e
            );
            error('Error exchanging cross-domain token.', e);
            $location.path('/signin');
        });
});

const CrossDomainTokenController = [
    '$rootScope',
    '$window',
    '$location',
    '$scope',
    'security',
    'signalboost',
    'loginService',
    'ssoRedirect',
    'logService',
    function (
        $rootScope,
        $window,
        $location,
        $scope,
        security,
        signalboost,
        loginService,
        ssoRedirect,
        logService
    ) {
        const $ctrl = this;

        $scope.processCrossDomainToken = (token) =>
            processCrossDomainTokenOnce(token, {
                $rootScope,
                $window,
                $location,
                security,
                signalboost,
                loginService,
                ssoRedirect,
                logService,
            });

        $ctrl.$onInit = () => {
            $scope.processCrossDomainToken($ctrl.crossDomainToken).finally(() => {
                $scope.$applyAsync();
            });
        };
    },
];

// there is no ui for the cross-domain token exchange
// it's just a redirect to user's homepage
// with an intermediate step of exchanging
// the token for an access token
export default {
    bindings: { crossDomainToken: '<' },
    templateUrl: crossDomainAuthTemplateUrl,
    controller: CrossDomainTokenController,
};
